// astro vue will render empty page if import vue from 'vue'
// @ts-ignore
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import { computed, ref, provide, type VNodeRef, onMounted } from 'vue'
import {
	checkProductSharingEnable,
	checkExpressCheckoutEnable,
	getProductSharingUrl,
	productSharingData,
	productSharingPostPerPage,
} from '@/legacy/ts/productSharing'
import { instanceProductSharing } from '@/assets/ts/apiManagement'
import { isDesktop } from '@/legacy/ts/checkDevice'
import goErrorPage from '@/legacy/ts/goErrorPage'
import useProductSharing from '@/legacy/ts/useProductSharing'
import useSnackBar from '@/legacy/ts/useSnackBar'
import BsSnackBar from '@/legacy/components/SnackBar.vue'
import usePostCard from '@/legacy/ts/usePostCard'
import { fetchPricingCurrencyBuyforyou } from '@/legacy/ts/buyforyou'
import BsLoading from '@/legacy/components/Loading.vue'
import BsGoBack from '@/legacy/components/GoBack.vue'
import BsDialogAction from '@/legacy/components/DialogAction.vue'
import ProductSharingTabs from '@/legacy/components/ProductSharingTabs.vue'
import ProductSharingProfile from '@/legacy/components/ProductSharingProfile.vue'
import ProductSharingPostList from '@/legacy/components/ProductSharingPostList.vue'
import ProductSharingPostCard from '@/legacy/components/ProductSharingPostCard.vue'
import ProductSharingEmptyContent from '@/legacy/components/ProductSharingEmptyContent.vue'
import hybridPage from '@/lib/hybridPage'
import {
	type IProductSharingMemberInfo,
	type TypeUpdateUserProfilePayload,
} from '@/types/ProductSharing'
import { copyText } from '@/lib/utils'

hybridPage()

createApp({
	name: 'Product-Sharing-Profile',
	components: {
		BsLoading,
		BsGoBack,
		BsSnackBar,
		BsDialogAction,
		ProductSharingTabs,
		ProductSharingProfile,
		ProductSharingPostList,
		ProductSharingPostCard,
		ProductSharingEmptyContent,
	},
	setup() {
		const { postCardTextInfo, postListTextInfo, pageContent } = window
		const { profileTextInfo } = pageContent
		const { isShow, messages, dismiss, addMessage } = useSnackBar()
		const postListRef = ref<VNodeRef | null>(null)
		const {
			postDetailCurrent,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			DialogType,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			interestsList,
			hideDialogPostDetail,
			setPostActions,
		} = usePostCard(postListRef)
		const { getPSUsername, isUserVerified, setToken } = useProductSharing()

		provide('addMessage', addMessage)

		const localCurrencyInfo = ref(null)
		const isExpressCheckoutEnable = ref(checkExpressCheckoutEnable())

		async function setLocalCurrencyInfo() {
			localCurrencyInfo.value = await fetchPricingCurrencyBuyforyou()
		}

		const isLoading = ref(true)
		const isPrivate = ref(false)
		const isShowPostContentBlock = ref(false)
		const userProfile = ref<IProductSharingMemberInfo | null>(null)
		const productSharingEnable = checkProductSharingEnable()

		const columnCount = computed(() => (isDesktop.value ? 3 : 2))
		const username = computed(() => {
			if (!import.meta.env.PROD) {
				return getParameterByName('user', window.location.href)
			}
			const url = new URL(window.location.href)
			const profileName = url.pathname.split('/').filter(Boolean).at(-1)
			return profileName ? decodeURIComponent(profileName) : ''
		})

		async function shareProfile(): Promise<string> {
			const title = `Link for ${username.value} Profile`
			const url = getProductSharingUrl('profile', { name: username.value })
			const isShareSupported = navigator.share !== undefined

			try {
				// 優先嘗試分享功能
				if (isShareSupported) {
					await navigator.share({ title, url })
					return '分享成功'
				}

				// 不支持分享時複製文字
				await copyAndNotify(url)
				return '文字已複製'
			} catch (error) {
				// 當分享失敗但非用戶取消時，嘗試複製
				if (
					isShareSupported &&
					error instanceof Error &&
					error.name !== 'AbortError'
				) {
					try {
						await copyAndNotify(url)
						return '分享失敗，但文字已複製'
					} catch {
						addMessage(pageContent.copyMsg.fail)
						throw new Error('分享與複製都失敗')
					}
				} else if (
					error instanceof DOMException &&
					error.name === 'AbortError'
				) {
					return '分享被取消'
				} else {
					addMessage(pageContent.copyMsg.fail)
					throw new Error((error as Error).message || '操作被取消或失敗')
				}
			}
		}

		async function copyAndNotify(text: string): Promise<void> {
			await copyText(text)
			addMessage(pageContent.copyMsg.success)
		}

		function fetchProfile() {
			return new Promise((resolve, reject) => {
				instanceProductSharing
					.get(`/users/${encodeURIComponent(username.value)}`)
					.then(({ data: { data } }) => {
						if (!data) reject()
						getPSUsername().then(PSUsername => {
							if (data.username === PSUsername) {
								window.location.href = getProductSharingUrl('private-profile')
							}

							userProfile.value = {
								id: data.id,
								description: data.description,
								followerCount: data.follower_count,
								followingCount: data.following_count,
								image: data.image,
								interests: data.interests,
								isFollowed: data.is_followed,
								latestPostsImages: data.latest_posts_images,
								likeCount: data.like_count,
								bookmarkCount: data.bookmark_count,
								memberTypes: data.member_types,
								site: data.site,
								totalContributions: data.total_contributions,
								username: encodeURIComponent(data.username),
							}
							resolve(userProfile.value)
						})
					})
					.catch(err => {
						if (err.response && err.response.status === 404) {
							isShowPostContentBlock.value = true
							isLoading.value = false
						} else {
							goErrorPage()
						}
						reject()
					})
			})
		}

		function goPreviewPostDetail() {
			const queryString = window.location.search
			const params = new URLSearchParams(queryString)
			const postId = params.get('fromPostId')

			if (postId) {
				window.location.href = getProductSharingUrl('post', { id: postId })
			} else {
				window.location.href = getProductSharingUrl('recommended')
			}
		}

		function getParameterByName(name: string, url: string) {
			// get query paramerter contains special characters &
			const paramsStart = url.indexOf('?')
			if (paramsStart === -1) return ''

			const paramsString = url.slice(paramsStart + 1)
			const paramsArray = paramsString.split('&')

			let isCollecting = false
			let paramValue = ''

			paramsArray.forEach(param => {
				if (param.startsWith(`${name}=`)) {
					isCollecting = true
					paramValue += param.split('=')[1]
				} else if (isCollecting && !param.includes('=')) {
					paramValue += `&${param}`
				} else {
					isCollecting = false
				}
			})

			return paramValue ? decodeURIComponent(paramValue) : ''
		}

		function updateUserProfile<T extends keyof IProductSharingMemberInfo>(
			payload: TypeUpdateUserProfilePayload<T>,
		) {
			if (!userProfile.value) return
			payload.forEach(item => {
				userProfile.value![item.param] = item.val
			})
		}

		provide('updateUserProfile', updateUserProfile)

		onMounted(async () => {
			if (productSharingEnable) {
				if (productSharingData.isLogin && isUserVerified.value) {
					await setToken()
				}
				await fetchProfile()
				if (isExpressCheckoutEnable.value) {
					setLocalCurrencyInfo()
				}
				isLoading.value = false
			} else {
				isLoading.value = false
			}
		})

		return {
			productSharingEnable,
			isLoading,
			productSharingPostPerPage,
			postCardTextInfo,
			postListTextInfo,
			profileTextInfo,
			isShow,
			messages,
			postDetailCurrent,
			categoryMap,
			warehouseIdMap,
			interestsMap,
			supportedCurrencies,
			buyforyouSupportWarehouse,
			priceLocal,
			priceLocalPost,
			DialogType,
			isShowDialogPostDetail,
			isLoadingPostDetail,
			localCurrencyInfo,
			interestsList,
			columnCount,
			postListRef,
			isPrivate,
			isShowPostContentBlock,
			userProfile,
			dismiss,
			hideDialogPostDetail,
			setPostActions,
			goPreviewPostDetail,
			shareProfile,
		}
	},
}).mount('#bsMain')
